import React, { useState, useContext, Children } from 'react';
import classnames from 'classnames';

// Types
import {
  FormTypeType,
  QuestionType,
  QuestionTypeType,
  ReviewFormTemplateType,
} from 'Shared/types/ReviewFormTemplate.type';
import { ReviewTemplateType } from 'Shared/types/ReviewTemplates.type';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import { useQuestion } from './use-question';
import { useReviewTemplate } from 'Shared/utils/hooks/use-review-template';

// Context
import { ReviewTemplateContext } from '../ReviewTemplateContext';

// Components
import { WpContainer, WpContainerBody, WpContainerFooter } from 'Wpds/components/container';
import WpSelectV2, { StrictOption } from 'Wpds/components/select/WpSelectV2';
import { WpFormLayout } from 'Wpds/components/form';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import { WpCollapsible } from 'Wpds/components/collapsible';
import WpInlineError from 'Wpds/components/inline-error/WpInlineError';
import { WpRadioGroup, WpRadioInput } from 'Wpds/components/radio';
import MultilineDescription from 'Dashboard/components/ThreeSixtyReviewPartials/MultilineDescription/MultilineDescription';
import WpCheckbox from 'Wpds/components/checkbox/WpCheckbox';
import { WpTooltip, WpTooltipContent, WpTooltipTrigger } from 'Wpds/components/tooltip';

// Styles
import './QuestionWrapper.scss';

const FeedbackFormQuestionOptions: StrictOption[] = [
  { label: 'New Section + Description', value: QuestionTypeType.TITLE_DESCRIPTION },
  { label: 'Short Answer Question', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating Question', value: QuestionTypeType.RATING },
  { label: 'Attribute Selection Question', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection Question', value: QuestionTypeType.GIF },
];

const PeerReviewFormQuestionOptions: StrictOption[] = [
  { label: 'New Section + Description', value: QuestionTypeType.TITLE_DESCRIPTION },
  { label: 'Short Answer Question', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating Question', value: QuestionTypeType.RATING },
  { label: 'Attribute Selection Question', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection Question', value: QuestionTypeType.GIF },
];

const CompilationFormQuestionOptionsV1: StrictOption[] = [
  { label: 'Short Answer', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating', value: QuestionTypeType.RATING },
  { label: 'Adjectives Selection', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection', value: QuestionTypeType.GIF },
  { label: 'Rating Summary', value: QuestionTypeType.RATING_SUMMARY },
  { label: 'Adjectives Selection Summary', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY },
  { label: 'AI Summary', value: QuestionTypeType.HOLISTIC_SUMMARY },
];

const CompilationFormQuestionOptionsV2: StrictOption[] = [
  { label: 'New Section + Description', value: QuestionTypeType.TITLE_DESCRIPTION },
  { label: 'AI Summary', value: QuestionTypeType.HOLISTIC_SUMMARY },
  { label: 'Short Answer Question', value: QuestionTypeType.TEXTAREA },
  { label: 'Rating Question', value: QuestionTypeType.RATING },
  { label: 'Attribute Selection Question', value: QuestionTypeType.ADJECTIVES_FOR_PROPERTY },
  { label: 'Gif Selection Question', value: QuestionTypeType.GIF },
];

type Props = {
  question: QuestionType;
  reviewFormType: FormTypeType;
  reviewFormTemplates: ReviewFormTemplateType[];
  children?: React.ReactNode;
  onChange: Function;
  handleDeleteQuestion: Function;
  handleDuplicateQuestion: Function;
  handleMoveQuestion: Function;
  activeQuestionKey: string;
  setActiveQuestionKey: Function;
};

const QuestionWrapper = ({
  question,
  reviewFormType,
  reviewFormTemplates,
  children,
  onChange,
  handleDeleteQuestion,
  handleDuplicateQuestion,
  handleMoveQuestion,
  activeQuestionKey,
  setActiveQuestionKey,
}: Props) => {
  const { errors } = useQuestion({ question });
  const [header, setHeader] = useState(question.header);
  const template = useContext(ReviewTemplateContext) as ReviewTemplateType;

  const { isVersionOne } = useReviewTemplate({ slug: template.slug, withFetch: false });

  const [description, setDescription] = useState(question.description);
  const [selfReviewDescription, setSelfReviewDescription] = useState(question.selfReviewDescription);
  const [visibility, setVisibility] = useState(question.visibility || 'everyone');

  const showSelfReviewDescriptionAndVisibility = reviewFormType === FormTypeType.peer_review && isVersionOne;

  const [optional, setOptional] = useState<boolean>(question?.optional !== undefined ? question?.optional : false);
  const [privateQuestion, setPrivateQuestion] = useState(question?.private !== undefined ? question?.private : false);
  const showPrivateQuestionCheckbox = reviewFormType === FormTypeType.manager_peer_review;
  const [isDeleting, toggleIsDeleting] = useToggle(false);

  const isActive = activeQuestionKey === question.key;

  const hasErrors = errors?.length > 0;

  const peerReviewFormTemplate = reviewFormTemplates.find(({ formType }) => formType == FormTypeType.peer_review);

  const isTitleAndDescription = question.type === QuestionTypeType.TITLE_DESCRIPTION;
  const placeholderTitle = isTitleAndDescription ? 'Section Header' : 'Question';

  const hasChildren = children && Children.count(children) > 0;

  const questionTypeOptions = {
    [FormTypeType.compilation]: isVersionOne ? CompilationFormQuestionOptionsV1 : CompilationFormQuestionOptionsV2,
    [FormTypeType.peer_review]: PeerReviewFormQuestionOptions,
    [FormTypeType.manager_peer_review]: PeerReviewFormQuestionOptions,
    [FormTypeType.self_peer_review]: PeerReviewFormQuestionOptions,
    [FormTypeType.feedback]: FeedbackFormQuestionOptions,
  }[reviewFormType];

  const handleUpdateType = value => {
    if (
      value === QuestionTypeType.RATING_SUMMARY &&
      !(
        peerReviewFormTemplate &&
        peerReviewFormTemplate.questions.find(question => question.type === QuestionTypeType.RATING)
      )
    ) {
      alert('You must create a rating question in the peer review before you can create a rating summary question.');
      return;
    }
    if (
      value === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY &&
      !(
        peerReviewFormTemplate &&
        peerReviewFormTemplate.questions.find(question => question.type === QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
      )
    ) {
      alert(
        'You must create an adjective question in the peer review before you can create a adjectives summary question.'
      );
      return;
    }

    const summarizerQuestion = reviewFormTemplates
      .map(reviewFormTemplate => reviewFormTemplate.questions)
      .flat()
      .find(({ summarizedQuestionKey }) => summarizedQuestionKey === question.key);
    if (summarizerQuestion) {
      if (
        (summarizerQuestion.type === QuestionTypeType.RATING_SUMMARY && value !== QuestionTypeType.RATING) ||
        (summarizerQuestion.type === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY &&
          value !== QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
      ) {
        alert(
          "You cannot change this question's type because it is linked to a summary question. Please delete the summary question first."
        );
        return;
      }
    }

    const additionalChanges =
      value === QuestionTypeType.RATING_SUMMARY || value === QuestionTypeType.ADJECTIVES_FOR_PROPERTY_SUMMARY
        ? {
            summarizedQuestionKey: peerReviewFormTemplate.questions.find(
              question =>
                question.type ===
                (value === QuestionTypeType.RATING_SUMMARY
                  ? QuestionTypeType.RATING
                  : QuestionTypeType.ADJECTIVES_FOR_PROPERTY)
            ).key,
          }
        : {};
    onChange({ type: value, ...additionalChanges });
  };

  const handleUpdateHeader = value => {
    setHeader(value);
    onChange({ header: value });
  };

  const handleUpdateDescription = value => {
    setDescription(value);
    onChange({ description: value });
  };

  const handleUpdateSelfReviewDescription = value => {
    setSelfReviewDescription(value);
    onChange({ selfReviewDescription: value });
  };

  const handleUpdateVisibility = value => {
    setVisibility(value);
    onChange({ visibility: value });
  };

  const handleUpdateOptional = (value: boolean) => {
    setOptional(value);
    onChange({ optional: value });
  };

  const handlePrivateQuestionOnChange = (value: boolean) => {
    setPrivateQuestion(value);
    onChange({ private: value });
  };

  const handleDeleteQuestionClick = () => {
    if (
      reviewFormTemplates.find(reviewFormTemplate =>
        reviewFormTemplate.questions.find(({ summarizedQuestionKey }) => summarizedQuestionKey === question.key)
      )
    ) {
      alert(
        'You cannot delete this question because it is linked to a summary question. Please delete the summary question first.'
      );
      return;
    }

    toggleIsDeleting();
    handleDeleteQuestion({ question });
  };

  const handleDuplicateClick = () => {
    handleDuplicateQuestion({ question });
  };

  const handleSetActiveQuestionKey = () => {
    setActiveQuestionKey(question.key);
  };

  const handleMoveUpClick = () => {
    handleMoveQuestion({ direction: 'up', question });
  };

  const handleMoveDownClick = () => {
    handleMoveQuestion({ direction: 'down', question });
  };

  const baseClassName = 'QuestionWrapper';
  const classes = classnames(baseClassName, isActive ? `${baseClassName}--active` : '');

  return (
    <div className={classes}>
      <div className={`${baseClassName}__wrapper`}>
        <WpContainer
          className={`${baseClassName}__wrapper__container`}
          onClick={!isActive ? handleSetActiveQuestionKey : null}
        >
          <WpContainerBody>
            {hasErrors && (
              <div className={`${baseClassName}__error`}>
                <span className={`${baseClassName}__error__icon`}>
                  <WpIcon name="exclamation-circle" />
                </span>
                <WpInlineError error={errors[0].label} />
              </div>
            )}
            {!isActive && (
              <div className={`${baseClassName}__inactive`}>
                <p className={`${baseClassName}__inactive__title`}>
                  {header || placeholderTitle}
                  {!optional && <span className={`${baseClassName}__inactive__title__required`}>*</span>}
                </p>
                {description && <MultilineDescription description={description} />}
              </div>
            )}
            <WpCollapsible open={isActive}>
              <WpFormLayout direction="vertical" spacing="md">
                <div className={`${baseClassName}__question-select`}>
                  <WpSelectV2
                    id={`question_type_${question.key}`}
                    label="Content type"
                    placeholder="Select question type"
                    options={questionTypeOptions}
                    value={question.type}
                    onChange={handleUpdateType}
                    forceReset
                  />
                </div>
                <WpTextField
                  id={`title_${question.key}`}
                  label={placeholderTitle}
                  value={header}
                  placeholder={placeholderTitle}
                  onChange={handleUpdateHeader}
                  required
                />
                <WpTextField
                  id={`description_${question.key}`}
                  label="Description"
                  value={description}
                  placeholder="Description"
                  multiline={2}
                  onChange={handleUpdateDescription}
                />
                {showSelfReviewDescriptionAndVisibility && (
                  <>
                    <WpTextField
                      id={`self_review_description_${question.key}`}
                      label="Description for self feedback"
                      value={selfReviewDescription}
                      placeholder="Self Feedback Description"
                      multiline={2}
                      onChange={handleUpdateSelfReviewDescription}
                    />
                    <WpRadioGroup
                      id={`visibility_${question.key}`}
                      label="Who should this question be shown to?"
                      defaultValue={visibility}
                      onChange={handleUpdateVisibility}
                      direction="horizontal"
                    >
                      <WpRadioInput
                        id={`self_and_non_self_review_visibility_${question.key}`}
                        label="All feedback providers"
                        value={'everyone'}
                      />
                      <WpRadioInput
                        id={`self_review_visibility_${question.key}`}
                        label="Self feedback providers"
                        value="self_review"
                      />
                      <WpRadioInput
                        id={`non_self_review_visibility_${question.key}`}
                        label="Non self feedback providers"
                        value="non_self_review"
                      />
                    </WpRadioGroup>
                  </>
                )}
              </WpFormLayout>
            </WpCollapsible>
            {hasChildren && children}
          </WpContainerBody>
          {isActive && (
            <WpContainerFooter justifyContent="spaceBetween">
              {isTitleAndDescription ? (
                // empty div to maintain flex alignment
                <div />
              ) : (
                <div className={`${baseClassName}__footer__optional-section`}>
                  <WpCheckbox
                    id={`optional_toggle_${question.key}`}
                    label="Optional"
                    checked={optional}
                    onChange={handleUpdateOptional}
                  />
                  {showPrivateQuestionCheckbox && (
                    <WpTooltip>
                      <WpTooltipTrigger>
                        <div>
                          <WpCheckbox
                            id={`private_question_${question.key}`}
                            label="Private question"
                            checked={privateQuestion}
                            onChange={handlePrivateQuestionOnChange}
                          />
                        </div>
                      </WpTooltipTrigger>
                      <WpTooltipContent>
                        Only the manager and Admins will be able to see the response. It will not be shared with the
                        review recipient.
                      </WpTooltipContent>
                    </WpTooltip>
                  )}
                </div>
              )}
              <WpButtonGroup>
                <WpButton variant="tertiary" onClick={handleDuplicateClick}>
                  <WpIcon name="copy" />
                </WpButton>
                <WpButton variant="tertiary" onClick={handleDeleteQuestionClick} loading={isDeleting}>
                  <WpIcon name="trash" />
                </WpButton>
              </WpButtonGroup>
            </WpContainerFooter>
          )}
        </WpContainer>
        <WpContainer className={`${baseClassName}__move-buttons`}>
          <WpContainerBody padding="025">
            <WpButtonGroup stacked spacing="sm">
              <WpButton variant="tertiary" onClick={handleMoveUpClick} size="sm">
                <WpIcon name="chevron-up" />
              </WpButton>
              <WpButton variant="tertiary" onClick={handleMoveDownClick} size="sm">
                <WpIcon name="chevron-down" />
              </WpButton>
            </WpButtonGroup>
          </WpContainerBody>
        </WpContainer>
      </div>
    </div>
  );
};

export default QuestionWrapper;
